// combatpro/views/ResistanceView.js
//
// Stage 76 (Resistance View): migrated from the Stage 33 placeholder shell
// (which rendered state.resistanceItems.map - the Stage 24 empty placeholder) to
// the legacy active-monster Resistance page (battletracker.html).
//
// Full-page scope (approved Option 2). The legacy view-resistance reparented
// resistance-block-${index} (the standalone Resistance Calculator, 6325-6357)
// into vi-resistance-mount (vi_mountResistance, 10124-10154). The resistance /
// immunity / vulnerability DATA fields lived in the monster detail area
// (textareas wired to updateMonsterField, legacy 5884/5888). This view brings
// both together so the page is self-contained.
//
// Renders ONLY the active monster (state.currentCombatantIndex) into
// #vi-resistance-mount; empty mount when no active monster - matching the
// per-monster view pattern used since Stage 66.
//
// Responsibilities (legacy parity):
//   - Resistances / Immunities / Vulnerabilities textareas -> updateMonsterField
//     (CombatContext 370-377, reused unchanged). The React monster factory
//     initialises immunities and resistances; vulnerabilities has no factory
//     default, so it is read with a || '' guard exactly as legacy did
//     (target.vulnerabilities || '', 7061) and written via updateMonsterField's
//     generic { ...m, [field]: value } setter. Missing default degrades safely
//     to "no vulnerability".
//   - Standalone calculator: Damage Amount + Damage Type -> computeFinalDamage
//     (extended in Stage 76 to take vulnerabilities as an additive 5th param),
//     producing a four-state result (immune / resistant / vulnerable / normal)
//     mirroring calculateStandaloneResistance (7043-7108). The legacy result was
//     an imperative innerHTML + inline-style write into calcResult-${index};
//     here it is declarative. No DOM writes.
//
// Damage math is owned by damageEngine.computeFinalDamage - NOT recomputed here.
// The view only derives the status label / colours around the engine's number,
// matching the legacy status text. Damage-type list imported from the canonical
// damage/damageTypes.js module. Calculator input is view-local state (the
// ephemeral damage amount + selected type), like DiceView's selection - not
// CombatContext state.
//
// No getElementById, no innerHTML, no reparenting, no localStorage, no API, no
// optional chaining, no em dash in source.

import React, { useContext, useState } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { DAMAGE_TYPES } from '../damage/damageTypes';
import { computeFinalDamage } from '../damage/damageEngine';

function ResistanceView() {
  const { state, updateMonsterField } = useContext(CombatContext);

  // Calculator input is view-local and ephemeral. Default type is the first
  // legacy option (Slashing), matching the select's default selection.
  const [calcDamage, setCalcDamage] = useState('');
  const [calcType, setCalcType] = useState(DAMAGE_TYPES[0]);

  const index = state.currentCombatantIndex;
  const monster = (index !== null && index !== undefined && state.monsters)
    ? state.monsters[index]
    : null;

  // No active monster: empty mount, faithful to the per-monster view pattern.
  if (!monster) {
    return (
      <div className="vi-view" id="view-resistance">
        <div id="vi-resistance-mount"></div>
      </div>
    );
  }

  const resistances = monster.resistances || '';
  const immunities = monster.immunities || '';
  const vulnerabilities = monster.vulnerabilities || '';

  // Build the calculator result declaratively. Mirrors
  // calculateStandaloneResistance: 0/empty -> no result; otherwise the engine
  // gives the final number and the view derives the four-state status label /
  // colour. Priority immunity -> resistance -> vulnerability -> normal is owned
  // by computeFinalDamage; the view re-derives only the LABEL by comparing the
  // engine output to the original, matching the legacy status copy.
  const originalDamage = parseInt(calcDamage, 10);
  let result = null;
  if (!isNaN(originalDamage) && originalDamage > 0) {
    const finalDamage = computeFinalDamage(
      originalDamage,
      calcType,
      resistances,
      immunities,
      vulnerabilities
    );

    let statusText;
    let statusColor;
    let statusBg;
    if (finalDamage === 0) {
      statusText = '\uD83D\uDEE1\uFE0F IMMUNE\n' + originalDamage + ' ' + calcType + ' \u2192 0 damage';
      statusColor = '#3498db';
      statusBg = 'rgba(52, 152, 219, 0.15)';
    } else if (finalDamage < originalDamage) {
      statusText = '\uD83D\uDD30 RESISTANT\n' + originalDamage + ' ' + calcType + ' \u2192 ' + finalDamage + ' damage (halved)';
      statusColor = '#f39c12';
      statusBg = 'rgba(243, 156, 18, 0.15)';
    } else if (finalDamage > originalDamage) {
      statusText = '\u26A0\uFE0F VULNERABLE\n' + originalDamage + ' ' + calcType + ' \u2192 ' + finalDamage + ' damage (doubled)';
      statusColor = '#e74c3c';
      statusBg = 'rgba(231, 76, 60, 0.15)';
    } else {
      statusText = '\u2713 NORMAL\n' + originalDamage + ' ' + calcType + ' \u2192 ' + finalDamage + ' damage';
      statusColor = '#27ae60';
      statusBg = 'rgba(39, 174, 96, 0.15)';
    }

    result = {
      text: statusText,
      color: statusColor,
      background: statusBg,
      border: '2px solid ' + statusColor
    };
  }

  // Render newline-separated status text as lines (legacy replaced \n with <br>
  // before innerHTML; here we split and render declaratively, no innerHTML).
  function renderStatusLines(text) {
    var lines = text.split('\n');
    return lines.map(function (line, i) {
      return <div key={i}>{line}</div>;
    });
  }

  return (
    <div className="vi-view" id="view-resistance">
      <div id="vi-resistance-mount">
        <div id={'resistance-block-' + index}>

          <div className="vi-resistance-data">
            <div className="vi-rc-field">
              <label htmlFor={'resistances-' + index}>Resistances</label>
              <textarea
                className="vi-rc-textarea"
                id={'resistances-' + index}
                placeholder="e.g., Cold, Lightning"
                value={resistances}
                onChange={(e) => updateMonsterField(index, 'resistances', e.target.value)}
              />
            </div>

            <div className="vi-rc-field">
              <label htmlFor={'immunities-' + index}>Immunities</label>
              <textarea
                className="vi-rc-textarea"
                id={'immunities-' + index}
                placeholder="e.g., Fire, Poison"
                value={immunities}
                onChange={(e) => updateMonsterField(index, 'immunities', e.target.value)}
              />
            </div>

            <div className="vi-rc-field">
              <label htmlFor={'vulnerabilities-' + index}>Vulnerabilities</label>
              <textarea
                className="vi-rc-textarea"
                id={'vulnerabilities-' + index}
                placeholder="e.g., Radiant, Fire"
                value={vulnerabilities}
                onChange={(e) => updateMonsterField(index, 'vulnerabilities', e.target.value)}
              />
            </div>
          </div>

          <div className="vi-resistance-calc">
            <div className="vi-resistance-title">{'\uD83E\uDDEE Resistance Calculator'}</div>
            <div className="vi-hint vi-text-center">
              Enter damage amount and type, then the result applies this monster's resistances, immunities, and vulnerabilities automatically
            </div>
            <div className="vi-resistance-grid">
              <div>
                <label className="vi-calc-label">Damage Amount</label>
                <input
                  type="number"
                  id={'calcDamage-' + index}
                  placeholder="20"
                  min="0"
                  className="vi-calc-input"
                  value={calcDamage}
                  onChange={(e) => setCalcDamage(e.target.value)}
                />
              </div>
              <div>
                <label className="vi-calc-label">Damage Type</label>
                <select
                  id={'calcDamageType-' + index}
                  className="vi-calc-select"
                  value={calcType}
                  onChange={(e) => setCalcType(e.target.value)}
                >
                  {DAMAGE_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>
            <div
              id={'calcResult-' + index}
              className="vi-resistance-result"
              style={result === null
                ? { display: 'none' }
                : { display: 'block', color: result.color, background: result.background, border: result.border }
              }
            >
              {result === null ? null : renderStatusLines(result.text)}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ResistanceView;
